import { SchedulerItemPreference, SchedulerPreference } from '@demind-inc/core';
import { SchedulerPrefTimeRange } from '../..';
import { inferEndDayOffset } from '../../../../data-access';

export interface WorkHourPrefItemState {
  start: SchedulerPrefTimeRange;
  end: SchedulerPrefTimeRange;
  endDayOffset?: SchedulerItemPreference['endDayOffset'];
}

export const initialDefaultWorkHourPrefItemState: WorkHourPrefItemState = {
  start: {
    time: '09:00',
    minimumTime: '00:00',
    maximumTime: '18:00',
  },
  end: {
    time: '18:00',
    minimumTime: '09:00',
    maximumTime: '23:59',
  },
};

export const workHourPrefItemReducer = (
  state: WorkHourPrefItemState,
  action: {
    type: 'workTime' | 'initialize';
    payload: Partial<WorkHourPrefItemState>;
  }
): WorkHourPrefItemState => {
  if (action.type === 'initialize') {
    return action.payload as WorkHourPrefItemState;
  }

  switch (action.type) {
    case 'workTime': {
      const workTimePayload = action.payload as Partial<WorkHourPrefItemState>;
      const updates: Partial<WorkHourPrefItemState> = {};

      if (workTimePayload.start) {
        updates.start = { ...state.start, ...workTimePayload.start };
      }
      if (workTimePayload.end) {
        updates.end = { ...state.end, ...workTimePayload.end };
      }
      if (workTimePayload.endDayOffset !== undefined) {
        updates.endDayOffset = workTimePayload.endDayOffset;
      }

      return { ...state, ...updates };
    }
    default:
      return state;
  }
};

export const transformDBPrefToWorkHourPref = (pref: SchedulerPreference) => {
  const start = pref.workTime?.start ?? initialDefaultWorkHourPrefItemState.start.time;
  const end = pref.workTime?.end ?? initialDefaultWorkHourPrefItemState.end.time;
  const endDayOffset = inferEndDayOffset(start, end, pref.workTime?.endDayOffset);

  return {
    ...initialDefaultWorkHourPrefItemState,
    endDayOffset,
    ...(pref.workTime?.start
      ? {
          start: {
            ...initialDefaultWorkHourPrefItemState.start,
            time: pref.workTime.start,
            defaultTime: pref.workTime.start,
          },
        }
      : {}),
    ...(pref.workTime?.end
      ? {
          end: {
            ...initialDefaultWorkHourPrefItemState.end,
            time: pref.workTime.end,
            defaultTime: pref.workTime.end,
          },
        }
      : {}),
  };
};

export const transformWorkHourPrefToDB = (
  prefEditState: WorkHourPrefItemState
): Partial<SchedulerPreference> => {
  const endDayOffset = inferEndDayOffset(
    prefEditState.start.time,
    prefEditState.end.time,
    prefEditState.endDayOffset
  );

  return {
    workTime: {
      start: prefEditState.start.time,
      end: prefEditState.end.time,
      endDayOffset,
    },
  };
};
